import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
  User as FirebaseUser, // Rename Firebase User type
  updateProfile
} from 'firebase/auth';
import {
  getFirestore,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  Timestamp, // Import Timestamp
  serverTimestamp, // Import serverTimestamp for creation time
  FieldValue // Import FieldValue
} from 'firebase/firestore';
import { auth, db } from '../firebase'; // Import initialized Firebase services
import { SUBSCRIPTION_TIERS, hasAccessToTier } from '../components/UserSubscriptionManager';

// Define structure for Firestore user document
interface UserProfile {
  uid: string;
  email: string | null;
  fullName?: string;
  role?: 'user' | 'coach' | 'admin'; // Add role
  // Use Timestamp for reading, but allow FieldValue during creation
  createdAt?: Timestamp | FieldValue | null; 
  lastLogin?: Timestamp | FieldValue | null;
  subscription?: Subscription;
  // Add other fields as needed based on your Firestore structure
  appointments?: number; // Example from screenshot
}

// Define Tier type alias locally and EXPORT it
export type Tier = 'Self' | 'Premium' | 'Complete' | 'None';

// Updated Subscription interface
interface Subscription {
  tier: Tier; // Use the locally defined Tier type
  status: 'active' | 'inactive' | 'pending';
  premium_end_date?: Timestamp | null; // Match screenshot field name and use Timestamp
  activationCode?: string; // Keep if you still use activation codes temporarily
}

// Combine Firebase User and UserProfile for currentUser state
type CurrentUser = FirebaseUser & UserProfile;

// Add these properties to your user interface
export interface User {
  // ... existing properties ...
  subscriptionTier?: string;
  subscriptionExpiry?: string;
  // ... other properties ...
}

// Fix the AuthContextType interface to include all required properties
interface AuthContextType {
  currentUser: CurrentUser | null;
  loading: boolean;
  loginWithGoogle: () => Promise<boolean>;
  loginWithEmail: (email: string, password: string) => Promise<boolean>;
  createAccount: (email: string, password: string, fullName: string) => Promise<boolean>;
  logout: () => void;
  isPremiumUser: () => boolean;
  updateUserPremium: (userId: string, targetTier: Tier, expiresAt?: Date | Timestamp) => Promise<boolean>;
  isCoach: () => boolean;
  userSubscription: {
    tier: string;
    expiry: string | null;
    isActive: boolean;
  };
  hasAccessToTier: (requiredTier: string) => boolean;
  refreshUserSubscription: () => Promise<void>;
}

const defaultContext: AuthContextType = {
  currentUser: null,
  loading: true, 
  loginWithGoogle: async () => false,
  loginWithEmail: async () => false,
  createAccount: async () => false,
  logout: () => { /* Default no-op, implemented in provider */ },
  isPremiumUser: () => false,
  updateUserPremium: async () => false,
  isCoach: () => false,
  userSubscription: {
    tier: SUBSCRIPTION_TIERS.NONE,
    expiry: null,
    isActive: false
  },
  hasAccessToTier: () => false,
  refreshUserSubscription: async () => {
    console.log("This is default implementation - actual implementation in provider");
  }
};

const AuthContext = createContext<AuthContextType>(defaultContext);

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true); // Initialize loading state
  const [userSubscription, setUserSubscription] = useState<{
    tier: string;
    expiry: string | null;
    isActive: boolean;
  }>({
    tier: SUBSCRIPTION_TIERS.NONE,
    expiry: null,
    isActive: false
  });

  // --- Firestore Helper ---
  const getUserProfile = async (uid: string): Promise<UserProfile | null> => {
    const userDocRef = doc(db, 'SAW_users', uid);
    const userDocSnap = await getDoc(userDocRef);
    if (userDocSnap.exists()) {
      // Explicitly cast to UserProfile for type safety
      return { uid, ...userDocSnap.data() } as UserProfile;
    } else {
      console.log("No such user profile document!");
      return null;
    }
  };

  // --- Firebase Auth State Listener ---
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log("Auth state changed. Firebase user:", user);
      setLoading(true);
      if (user) {
        const profile = await getUserProfile(user.uid);
        console.log("Fetched profile:", profile);

        if (profile) {
          // Combine Firebase user object with Firestore profile data
          setCurrentUser({ ...user, ...profile });
          console.log("Current user SET with profile:", { ...user, ...profile });

          // Update last login time (optional, fire and forget)
          const userDocRef = doc(db, 'SAW_users', user.uid);
          updateDoc(userDocRef, { lastLogin: serverTimestamp() }).catch(err => console.error("Error updating last login:", err));

        } else {
          // User authenticated but no Firestore profile exists - CREATE one
          console.error("User authenticated but no profile found for UID:", user.uid, ". Creating fallback profile.");
          const userDocRef = doc(db, 'SAW_users', user.uid);
          const fallbackProfile: UserProfile = {
            uid: user.uid,
            email: user.email,
            fullName: user.displayName || 'Nieuwe Gebruiker', // Use display name from auth if available
            role: 'user',
            createdAt: serverTimestamp(),
            lastLogin: serverTimestamp(),
            subscription: { tier: 'None', status: 'active', premium_end_date: null }
          };
          try {
            await setDoc(userDocRef, fallbackProfile);
            console.log("Fallback profile created in Firestore for UID:", user.uid);
            // Now set the local state with this newly created profile
            setCurrentUser({ ...user, ...fallbackProfile });
            console.log("Current user SET with fallback profile:", { ...user, ...fallbackProfile });
          } catch (error) {
            console.error("Failed to create fallback profile in Firestore:", error);
            // If Firestore creation fails, maybe just set local state without profile?
            // Or sign out? For now, setting basic local state
             setCurrentUser({
               ...user,
               uid: user.uid, 
               email: user.email,
               // Cannot provide full profile here as Firestore write failed
             } as CurrentUser); // Cast necessary if UserProfile fields are mandatory
             console.log("Current user SET with basic info ONLY after Firestore fallback failure.");
          }
        }
      } else {
        // User is signed out
        console.log("User signed out, setting currentUser to null");
        setCurrentUser(null);
      }
      console.log("Auth loading finished.");
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // --- Authentication Functions ---

  const loginWithEmail = async (email: string, password: string): Promise<boolean> => {
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      // Auth state listener will handle setting currentUser and profile
      setLoading(false);
      return true;
    } catch (error) {
      console.error('Error logging in with email:', error);
      setLoading(false);
      return false;
    }
  };

  const createAccount = async (email: string, password: string, fullName: string): Promise<boolean> => {
    setLoading(true); // Set loading before async operation
    try {
      // 1. Create the user in Firebase Authentication
      // We pass fullName here, but Firebase Auth doesn't directly use it for email/password creation.
      // The Cloud Function trigger will use user.displayName if available from Auth providers (like Google),
      // or a default if not. We could potentially update the Auth profile here too, but the function handles the DB part.
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Optional: Update the Firebase Auth user's display name immediately
      // This makes it potentially available to the Cloud Function trigger if it runs quickly
      // It might also be useful if you show the display name in the UI right after signup
      try {
          // NOTE: Need to import updateProfile from firebase/auth
          // Assuming 'updateProfile' is imported at the top like:
          // import { ..., updateProfile } from 'firebase/auth';
          await updateProfile(user, { displayName: fullName });
          console.log(`Updated Auth profile displayName for UID: ${user.uid}`);
      } catch (updateError) {
          console.error(`Error updating Auth profile displayName for UID: ${user.uid}`, updateError);
          // Continue even if display name update fails, profile creation is separate
      }


      // 2. Firestore profile creation is now handled by the Cloud Function trigger `createUserProfile`.
      // REMOVED Firestore setDoc logic from here.
      // const userDocRef = doc(db, 'SAW_users', user.uid);
      // const newUserProfileData = { ... };
      // console.log(`Attempting to create Firestore profile for UID: ${user.uid}`);
      // await setDoc(userDocRef, newUserProfileData);
      // console.log(`Successfully created Firestore profile for UID: ${user.uid}`);

      console.log(`Firebase Auth user created successfully: UID=${user.uid}. Cloud Function will handle Firestore profile.`);

      // Auth state listener will eventually pick up the new user and attempt to fetch the profile
      // (which should exist shortly after the Cloud Function runs).
      // setLoading(false); // <<< Let the listener handle the final loading state
      return true;
    } catch (error) {
      console.error('Error during Auth account creation:', error); // Updated error message
      // Check for specific auth errors like email-already-in-use
       if (typeof error === 'object' && error !== null && 'code' in error) {
         const firebaseError = error as { code?: string, message?: string };
         console.error(`Auth Error Code: ${firebaseError.code}, Message: ${firebaseError.message}`);
         // You could provide more specific user feedback based on the code here
         if (firebaseError.code === 'auth/email-already-in-use') {
             // Handle this specific error, e.g., set an error message for the UI
             console.error('Attempted to register with an email that is already in use.');
         }
       }
      setLoading(false); // Set loading false on error
      return false;
    } finally {
       // Ensure loading is eventually set to false if the listener doesn't
       // setLoading(false); // Relying on listener
    }
  };

  const loginWithGoogle = async (): Promise<boolean> => {
    setLoading(true);
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const userDocRef = doc(db, 'SAW_users', user.uid);
      const docSnap = await getDoc(userDocRef);

      if (!docSnap.exists()) {
        // Profile doesn't exist, create it (including Firestore doc)
        console.log(`Creating profile for new Google user: ${user.uid}`);
        const newUserProfileData: UserProfile = {
          uid: user.uid,
          email: user.email,
          fullName: user.displayName || 'Google Gebruiker', 
          role: 'user',
          createdAt: serverTimestamp(),
          lastLogin: serverTimestamp(),
          subscription: { tier: 'None', status: 'active', premium_end_date: null }
        };
        try {
            await setDoc(userDocRef, newUserProfileData);
            console.log("Firestore profile created for Google user:", user.uid);
            // Auth listener will likely pick this up, but setting state here ensures faster UI update
            // setCurrentUser({ ...user, ...newUserProfileData }); 
        } catch (error) {
            console.error("Failed to create Firestore profile for Google user:", error);
            // Handle error - maybe prevent login? For now, log and continue
        }
      } else {
          // Profile exists, update last login (optional - auth listener also does this)
          updateDoc(userDocRef, { lastLogin: serverTimestamp() }).catch(err => console.error("Error updating last login from Google sign in:", err));
      }
      // Auth state listener will handle setting the final currentUser state
      setLoading(false); 
      return true;
    } catch (error) {
      console.error('Error logging in with Google:', error);
      setLoading(false);
      return false;
    }
  };

  // --- Logout Function ---
  const logout = async () => {
    try {
      await signOut(auth);
      // The onAuthStateChanged listener will handle setting currentUser to null
      // and setLoading to false eventually.
      console.log("User successfully signed out.");
    } catch (error) {
      console.error("Error signing out: ", error);
      // Optionally handle logout errors (e.g., display a message to the user)
    }
  };

  // Check if the current user has an active premium subscription based on Firestore data
  const isPremiumUser = (): boolean => {
    if (!currentUser?.subscription) return false;

    const sub = currentUser.subscription;
    const isPremiumTier = (sub.tier === 'Premium' || sub.tier === 'Complete') && sub.status === 'active';

    // Check expiry date if it exists
    if (isPremiumTier && sub.premium_end_date) {
        // Convert Firestore Timestamp to Date object for comparison
        const expiryDate = sub.premium_end_date.toDate();
        return expiryDate > new Date(); // Check if expiry date is in the future
    }

    return isPremiumTier; // Return true if premium and no expiry date is set (or handle differently)
  };

  // Check user role from Firestore data
   const isCoach = (): boolean => {
     return currentUser?.role === 'coach' || currentUser?.role === 'admin'; // Allow admins coach access too
   };

  // Admin/Coach function to update a specific user's premium status in Firestore
  const updateUserPremium = async (userId: string, targetTier: Tier, expiresAt?: Date | Timestamp): Promise<boolean> => {
    // Optional: Add check to ensure current user is a coach/admin before allowing this
     if (!isCoach()) {
       console.error("Permission denied: Only coaches or admins can update user premium status.");
       return false;
     }

    setLoading(true); // Consider if loading state is needed here, might affect coach UI

    try {
      // Reference to user documents in both collections
      const sawUserDocRef = doc(db, 'SAW_users', userId);
      const userDocRef = doc(db, 'users', userId);

      let expiryTimestamp: Timestamp | null = null;
      let expiryISOString: string | null = null;

      // Calculate expiry only for subscription tiers (not 'None')
      if (targetTier !== 'None' && targetTier !== SUBSCRIPTION_TIERS.NONE) {
          // Determine expiry date: use provided value or default (e.g., 1 year from now)
          if (expiresAt) {
              expiryTimestamp = (expiresAt instanceof Timestamp) ? expiresAt : Timestamp.fromDate(expiresAt);
              expiryISOString = expiresAt instanceof Date ? expiresAt.toISOString() : expiresAt.toDate().toISOString();
          } else {
              const defaultExpiryDate = new Date();
              defaultExpiryDate.setFullYear(defaultExpiryDate.getFullYear() + 1);
              expiryTimestamp = Timestamp.fromDate(defaultExpiryDate);
              expiryISOString = defaultExpiryDate.toISOString();
          }
      } // For 'None' tier, both remain null

      // Update SAW_users collection (old format)
      // Construct the new subscription object based on the target tier
      const newSubscription: Subscription = {
          tier: targetTier,
          status: 'active', // Assuming setting a tier makes it active
          premium_end_date: expiryTimestamp
      };

      // Update the SAW_users document
      await updateDoc(sawUserDocRef, {
        subscription: newSubscription,
        // Also add the new format fields for compatibility
        subscriptionTier: targetTier,
        subscriptionExpiry: expiryISOString
      });

      // Update the users collection (new format)
      await updateDoc(userDocRef, {
        subscriptionTier: targetTier,
        subscriptionExpiry: expiryISOString,
        lastUpdated: new Date().toISOString()
      });

      // If updating the *currently logged-in* user's status, refresh their local state
      if (currentUser && userId === currentUser.uid) {
        const updatedProfile = await getUserProfile(userId);
        if (updatedProfile) {
           setCurrentUser({ ...currentUser, ...updatedProfile });
        }
      }

      console.log(`Successfully updated subscription tier for user ${userId} to ${targetTier}`);
      setLoading(false);
      return true;
    } catch (error) {
      console.error(`Error updating subscription tier for user ${userId}:`, error);
      setLoading(false);
      return false;
    }
  };

  // Add this function to fetch user subscription
  const fetchUserSubscription = async (userId: string) => {
    try {
      const userDocRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const tier = userData.subscriptionTier || SUBSCRIPTION_TIERS.NONE;
        const expiry = userData.subscriptionExpiry || null;
        
        // Check if subscription is active
        let isActive = false;
        if (expiry) {
          const expiryDate = new Date(expiry);
          const now = new Date();
          isActive = expiryDate > now;
        }
        
        setUserSubscription({
          tier,
          expiry,
          isActive
        });
      }
    } catch (error) {
      console.error('Error fetching user subscription:', error);
    }
  };

  // Add this function to check tier access
  const checkTierAccess = (requiredTier: string) => {
    if (!userSubscription.isActive) return false;
    return hasAccessToTier(userSubscription.tier, requiredTier);
  };

  // Add this effect to fetch subscription when user changes
  useEffect(() => {
    if (currentUser) {
      fetchUserSubscription(currentUser.uid);
    } else {
      // Reset subscription when user logs out
      setUserSubscription({
        tier: SUBSCRIPTION_TIERS.NONE,
        expiry: null,
        isActive: false
      });
    }
  }, [currentUser]);

  // Implement the refreshUserSubscription method
  const refreshUserSubscription = async () => {
    if (currentUser) {
      try {
        // Try to fetch from users collection first (new format)
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          // Use new format fields if available
          if (userData.subscriptionTier) {
            const tier = userData.subscriptionTier || SUBSCRIPTION_TIERS.NONE;
            const expiry = userData.subscriptionExpiry || null;
            
            // Check if subscription is active
            let isActive = false;
            if (expiry) {
              const expiryDate = new Date(expiry);
              const now = new Date();
              isActive = expiryDate > now;
            }
            
            setUserSubscription({
              tier,
              expiry,
              isActive
            });
            
            console.log("User subscription refreshed successfully from users collection");
            return; // Exit if we successfully get data from users collection
          }
        }
        
        // Fallback to SAW_users collection (old format)
        const sawUserDocRef = doc(db, 'SAW_users', currentUser.uid);
        const sawUserDoc = await getDoc(sawUserDocRef);
        
        if (sawUserDoc.exists()) {
          const userData = sawUserDoc.data();
          
          // First check new format fields in SAW_users
          if (userData.subscriptionTier) {
            const tier = userData.subscriptionTier || SUBSCRIPTION_TIERS.NONE;
            const expiry = userData.subscriptionExpiry || null;
            
            // Check if subscription is active
            let isActive = false;
            if (expiry) {
              const expiryDate = new Date(expiry);
              const now = new Date();
              isActive = expiryDate > now;
            }
            
            setUserSubscription({
              tier,
              expiry,
              isActive
            });
          } 
          // Otherwise use old subscription object format
          else if (userData.subscription) {
            const subscriptionData = userData.subscription;
            const tier = subscriptionData.tier || SUBSCRIPTION_TIERS.NONE;
            let expiry = null;
            
            if (subscriptionData.premium_end_date) {
              const premiumEndDate = subscriptionData.premium_end_date;
              if (premiumEndDate instanceof Timestamp) {
                expiry = premiumEndDate.toDate().toISOString();
              }
            }
            
            // Check if subscription is active
            let isActive = subscriptionData.status === 'active';
            if (isActive && expiry) {
              const expiryDate = new Date(expiry);
              const now = new Date();
              isActive = expiryDate > now;
            }
            
            setUserSubscription({
              tier,
              expiry,
              isActive
            });
          }
          else {
            // If no subscription data found, set to default NONE
            setUserSubscription({
              tier: SUBSCRIPTION_TIERS.NONE,
              expiry: null,
              isActive: false
            });
          }
          
          console.log("User subscription refreshed successfully from SAW_users collection");
        }
      } catch (error) {
        console.error("Error refreshing user subscription:", error);
        // On error, reset to default state
        setUserSubscription({
          tier: SUBSCRIPTION_TIERS.NONE,
          expiry: null,
          isActive: false
        });
      }
    }
  };

  // --- Context Value ---
  const value = {
    currentUser,
    loading,
    loginWithGoogle,
    loginWithEmail,
    createAccount,
    logout,
    isPremiumUser,
    updateUserPremium,
    isCoach,
    userSubscription,
    hasAccessToTier: checkTierAccess,
    refreshUserSubscription
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children} {/* Render children only when initial loading is complete */}
    </AuthContext.Provider>
  );
}; 